/**
 * RespaldoMaterial — La tarjeta "Material" de la OT (ciclo F1 + F4, 31-ago-2026).
 *
 * El vendedor cotiza al cliente después de pedirle precio a un proveedor. Ese PDF —el
 * respaldo del número que puso— vivía en su correo. Ahora se adjunta a la cotización, y
 * esto es lo que hace que sirva: el operador de producción lo ve en la OT **antes de
 * fabricar**, que es el momento en que importa saber si el material ya tiene precio y de
 * quién.
 *
 * F4 le suma la otra mitad: las COMPRAS ligadas a este trabajo y en qué van. La cadena
 * cotización → OT → compra → factura del proveedor existía en pedazos —el conciliador ya
 * cruza el RCV con las OC— pero no se podía recorrer desde donde se trabaja. Ahora el que
 * está mirando la OT ve si el material ya se pidió, a quién, si llegó y con qué factura.
 *
 * Es de SOLO LECTURA a propósito. Quien fabrica no edita el respaldo comercial de quien
 * vendió ni el estado de una compra; si algo está mal, se corrige donde se cargó.
 */
import React, { useEffect, useState } from "react";
import backend from "../../backend";

const clp = n => (n === null || n === undefined ? "—" : `$${Number(n).toLocaleString("es-CL")}`);
const fecha = d => (d ? new Date(d).toLocaleDateString("es-CL") : null);

const ESTADO_COMPRA = {
  cotizando: { texto: "esperando cotización", color: "#8a8a8a" },
  cotizado:  { texto: "cotizada",             color: "#a78bfa" },
  pedido:    { texto: "pedida",               color: "#f39c12" },
  enCamino:  { texto: "en camino",            color: "#f39c12" },
  recibido:  { texto: "recibida",             color: "#2ecc71" },
  cancelado: { texto: "descartada",           color: "#e74c3c" },
};

const RespaldoMaterial = ({ quotationId, workOrderId }) => {
  const [docs, setDocs] = useState([]);
  const [compras, setCompras] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    const pedidos = [];
    // Los dos bloques se piden en paralelo y cada uno falla solo: que no haya cotización
    // espejo no puede dejar sin ver las compras, y al revés tampoco.
    pedidos.push(
      quotationId
        ? backend.get(`document?quotationRefs=${quotationId}`)
            .then(({ data }) => {
              if (!vivo) return;
              const lista = Array.isArray(data) ? data : (data?.documents || []);
              setDocs(lista.filter(d => d.docType === "cotizacionProveedor" && !d.deletedAt));
            })
            .catch(() => {})
        : Promise.resolve()
    );
    const params = [];
    if (workOrderId) params.push(`workOrder=${workOrderId}`);
    if (quotationId) params.push(`quotation=${quotationId}`);
    pedidos.push(
      params.length
        ? backend.get(`purchaseOrder/de-trabajo?${params.join("&")}`)
            .then(({ data }) => { if (vivo) setCompras(data?.compras || []); })
            .catch(() => {})
        : Promise.resolve()
    );
    Promise.all(pedidos).finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [quotationId, workOrderId]);

  // Sin nada que mostrar no se dibuja nada: un bloque vacío que dice "sin datos" en cada
  // OT es ruido en la pantalla que más se mira del taller.
  if (cargando || (!docs.length && !compras.length)) return null;

  return (
    <div style={{
      background: "#161616", border: "1px solid #2e2e2e", borderRadius: 6,
      padding: "10px 14px", marginBottom: 12,
    }}>
      <div style={{ fontSize: 10, color: "#cbd5e1", textTransform: "uppercase",
                    letterSpacing: ".05em", marginBottom: 6 }}>
        💰 Material
      </div>
      {!!docs.length && (
        <div style={{ fontSize: 10, color: "#8a8a8a", marginBottom: 2 }}>Cotizado por ventas</div>
      )}
      {docs.map(d => {
        const c = d.cotizacionProveedor || {};
        const vencida = c.validezHasta && new Date(c.validezHasta) < new Date();
        return (
          <div key={d._id} style={{ display: "flex", gap: 10, alignItems: "baseline",
                                    flexWrap: "wrap", padding: "4px 0", borderTop: "1px solid #222" }}>
            <a href={d.url} target="_blank" rel="noreferrer" style={{ color: "#3498db", fontSize: 12 }}>
              {d.label || d.originalName}
            </a>
            {c.proveedor?.name && <span style={{ fontSize: 11, color: "#ddd" }}>{c.proveedor.name}</span>}
            {c.monto ? <span style={{ fontSize: 11, color: "#ddd" }}>{clp(c.monto)}</span> : null}
            {c.validezHasta && (
              <span style={{ fontSize: 11, color: vencida ? "#e74c3c" : "#8a8a8a" }}>
                {vencida ? "cotización vencida el " : "vale hasta "}{fecha(c.validezHasta)}
              </span>
            )}
          </div>
        );
      })}

      {/* F4 — las compras de este trabajo. Es lo que responde "¿el material ya se pidió?"
          sin salir de la OT. */}
      {!!compras.length && (
        <>
          <div style={{ fontSize: 10, color: "#8a8a8a", margin: "8px 0 2px" }}>
            Compras de este trabajo
          </div>
          {compras.map(c => {
            const e = ESTADO_COMPRA[c.status] || { texto: c.status, color: "#8a8a8a" };
            return (
              <div key={c._id} style={{ display: "flex", gap: 10, alignItems: "baseline",
                                        flexWrap: "wrap", padding: "4px 0", borderTop: "1px solid #222" }}>
                <span style={{ fontSize: 12, color: "#ddd" }}>OC N°{c.number}</span>
                <span style={{ fontSize: 11, color: "#ddd" }}>
                  {c.proveedor || <i style={{ color: "#777" }}>sin proveedor</i>}
                </span>
                <span style={{ fontSize: 11, color: e.color }}>{e.texto}</span>
                {c.total > 0 && <span style={{ fontSize: 11, color: "#8a8a8a" }}>{clp(c.total)}</span>}
                {c.status === "cotizando" && !c.rfqEnviado && (
                  <span style={{ fontSize: 11, color: "#f39c12" }}>· todavía no se le pidió al proveedor</span>
                )}
                {c.facturaProveedor && (
                  <span style={{ fontSize: 11, color: "#8a8a8a" }}>· factura {c.facturaProveedor}</span>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default RespaldoMaterial;
